import { useState } from 'react'
import { Box, styled } from '@mui/material'
import { EllipsisWithTooltip, Typography } from '@carto/meridian-ds/components'
import {
  Comment as CommentType,
  CommentResponse,
  CommentResponseActionType,
  CommentUser
} from 'features/collaboration/types'
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl'
import CommentContent from './CommentContent'
import ResolveButton from './ResolveButton'
import { useIntlMessages } from 'app/localization/useIntlMessages'
import CommentAuthorAvatar from './CommentAuthorAvatar'

const SHORTCUT_MAX_LINES = 3

export type CommentProps = {
  comment: CommentType | CommentResponse
  positionMarker?: React.ReactNode

  /// small is used in CommentsList, typygraphy is one step smaller than in the comment detail view
  size?: 'small'

  users: CommentUser[]

  /// only supported for size=`small` - it trims length of content to 3 lines, used in CommentsList
  shortcut?: boolean

  actions?: React.ReactNode
  onToggleResolved?: () => void
}

const CommentRoot = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1)
}))

const HeaderBox = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'min-content 1fr auto',
  alignItems: 'center',
  width: '100%'
}))

const UnreadIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: `3px`,
  width: theme.spacing(1),
  height: theme.spacing(1),
  backgroundColor: theme.palette.primary.main,
  borderRadius: '50%'
}))

const CommentMeta = styled(Box)(() => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  flexWrap: 'nowrap',
  textOverflow: 'ellipsis',
  gap: 0,
  overflow: 'hidden'
}))

const ActionsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(0.5)
}))

const messageIds = {
  unread: 'collaboration.comments.unread',
  editedTag: 'collaboration.comments.editedTag',
  deletedUser: 'collaboration.comments.deletedUser',
  resolved: 'collaboration.comments.actionTypes.resolved',
  reopened: 'collaboration.comments.actionTypes.reopened'
}

function Comment({ comment, positionMarker, actions, shortcut, size, onToggleResolved, users }: CommentProps) {
  const {
    createdAt: createdAtString,
    author: { email }
  } = comment

  const isThread = !('commentId' in comment)
  const resolved = isThread ? comment.resolved : undefined
  const actionType = isThread ? undefined : comment.actionType
  const isPseudoAction = actionType && actionType !== CommentResponseActionType.Reply
  const messages = useIntlMessages(messageIds)
  const [hovered, setHovered] = useState(false)
  const createdAt = new Date(createdAtString)
  const user = users.find((user) => user.user_id === comment.author.id)
  const noAccess = user?.noAccess
  const deletedUser = comment.author.deactivated
  const edited =
    !isPseudoAction &&
    (comment.edited ??
      // TODO: https://app.shortcut.com/cartoteam/story/482385
      // detect `edited` for replies
      // (for root comments updatedAt is changed on each response/resolve/etc)
      ('commentId' in comment ? comment.updatedAt !== comment.createdAt : false))

  const isSmall = size === 'small'
  const showActions = actions ? hovered : true
  const disabled = noAccess || deletedUser
  const showResolved = onToggleResolved && (resolved || hovered)
  const content = isPseudoAction && actionType ? messages[actionType] ?? actionType : comment.content

  const handleToggleResolved = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleResolved?.()
  }

  return (
    <CommentRoot
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ gap: isSmall ? 0.5 : 1 }}
    >
      <HeaderBox>
        {comment.read === false && <UnreadIndicator role="status" aria-label={messages.unread} />}
        <Box sx={{ flex: 0, pr: 1 }}>
          <CommentAuthorAvatar user={user} author={comment.author} disabled={resolved || disabled} />
        </Box>

        <CommentMeta>
          <Typography
            variant={isSmall ? 'caption' : 'button'}
            weight="medium"
            color={resolved || disabled ? 'text.secondary' : 'text.primary'}
            component="span"
          >
            <EllipsisWithTooltip>{deletedUser ? messages.deletedUser : email}</EllipsisWithTooltip>
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center', justifySelf: 'flex-end' }}>
            {positionMarker && (
              <>
                <Typography variant="caption" color="text.secondary" weight="medium">
                  {positionMarker}
                </Typography>
                <Typography color="black.25" component="span" variant="caption">
                  •
                </Typography>
              </>
            )}
            <Typography variant="caption" color="text.secondary" noWrap>
              <FormattedDate year="numeric" month="short" day="2-digit" value={createdAt} />
              {' - '}
              <FormattedTime value={createdAt} />
              {edited && ` ${messages.editedTag}`}
            </Typography>
          </Box>
        </CommentMeta>

        {(showActions || showResolved) && !isPseudoAction && (
          <ActionsBox>
            {showActions && actions}
            {showResolved && <ResolveButton resolved={resolved} onClick={handleToggleResolved} />}
          </ActionsBox>
        )}
      </HeaderBox>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <CommentContent
          content={content}
          maxLines={shortcut ? SHORTCUT_MAX_LINES : undefined}
          size={size}
          disabled={disabled || resolved}
          actionType={actionType}
          users={users}
        />
        {isThread && Boolean(comment.responsesCount) && (
          <Box>
            <Typography variant="caption" color={disabled || resolved ? 'text.secondary' : 'primary'} weight="medium">
              <FormattedMessage id="collaboration.comments.replies" values={{ count: comment.responsesCount }} />
            </Typography>
          </Box>
        )}
      </Box>
    </CommentRoot>
  )
}

export default Comment
